import { settingsBlockOf, withoutSettingsBlock } from './agent-settings-model';

/**
 * Who the agents speak for: the brand, the assistant's name, what the brand
 * is, and — on older solutions — the Knowledge Agent's tone and global
 * instructions. These lived in the full Supervisor configuration
 * (`SupervisorPanel`); Agent settings edits them now, written where the
 * solution's agents actually read them:
 *
 * - AI-Native template (`supervisor-knowledge.csv`): step 7005 sets
 *   `BRAND_NAME`, `SOLUTION_NAME` and `BRAND_DESCRIPTION`, which every AI
 *   step's prompt uses. Tone and instructions are part of the prompt itself.
 * - Older (Gemini) solutions: the solution version's fields, and the config
 *   row the backend marks `shouldSyncWithSupervisorConfig`.
 *
 * Pure: the panel reads and the save writes with these.
 */

export interface AgentIdentity {
  brandName: string;
  assistantName: string;
  description: string;
  /** Older solutions only: the Knowledge Agent's tone. */
  tone?: string;
  /** Older solutions only: the Knowledge Agent's global instructions. */
  instructions?: string;
}

export interface IdentityRow {
  id: string;
  nodeNumber?: number | null;
  command?: unknown;
  parameterInput?: unknown;
  settings?: unknown;
}

export type IdentityTarget<R extends IdentityRow> =
  | { kind: 'template'; row: R }
  | { kind: 'legacy'; row: R | null };

/** The solution-version fields older solutions keep these in. */
export interface IdentityFields {
  brandName?: string | null;
  supervisorName?: string | null;
  otherInformation?: string | null;
  tone?: string | null;
  knowledgeAgentContext?: string | null;
}

const parse = (raw: unknown): Record<string, unknown> | null => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
};

const setOf = (row: IdentityRow): Record<string, unknown> | null => {
  const set = parse(row.parameterInput)?.set;
  return set && typeof set === 'object' && !Array.isArray(set)
    ? (set as Record<string, unknown>)
    : null;
};

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/**
 * Where this solution's identity lives: the template's variables step when
 * it has one, else the older config row (or none, with only the fields).
 */
export const identityTarget = <R extends IdentityRow>(
  rows: R[]
): IdentityTarget<R> => {
  const template = rows.find(
    (r) =>
      String(r.command ?? '') === 'SysAssignVariable' &&
      setOf(r) !== null &&
      'BRAND_NAME' in (setOf(r) as object) &&
      'BRAND_DESCRIPTION' in (setOf(r) as object)
  );
  if (template) return { kind: 'template', row: template };
  const legacy = rows.find(
    (r) =>
      !!(r.settings as { shouldSyncWithSupervisorConfig?: boolean } | null)
        ?.shouldSyncWithSupervisorConfig
  );
  return { kind: 'legacy', row: legacy ?? null };
};

/** The identity as the solution has it now. */
export const readIdentity = <R extends IdentityRow>(
  target: IdentityTarget<R>,
  fields: IdentityFields | null | undefined
): AgentIdentity => {
  if (target.kind === 'template') {
    const set = setOf(target.row) ?? {};
    return {
      brandName: str(set.BRAND_NAME),
      assistantName: str(set.SOLUTION_NAME),
      description: str(set.BRAND_DESCRIPTION),
    };
  }
  return {
    brandName: str(fields?.brandName),
    assistantName: str(fields?.supervisorName),
    description: withoutSettingsBlock(fields?.otherInformation),
    tone: str(fields?.tone),
    instructions: withoutSettingsBlock(fields?.knowledgeAgentContext),
  };
};

/** The template step's parameters with the identity written in. */
export const templateParameterInput = (
  row: IdentityRow,
  identity: AgentIdentity
): string => {
  const params = parse(row.parameterInput) ?? {};
  return JSON.stringify({
    ...params,
    set: {
      ...(setOf(row) ?? {}),
      BRAND_NAME: identity.brandName.trim(),
      SOLUTION_NAME: identity.assistantName.trim(),
      BRAND_DESCRIPTION: identity.description.trim(),
    },
  });
};

/**
 * `text` with the settings block `original` carries, if it carries one: the
 * description and instructions share their field with Agent settings' own
 * choices, which must survive an edit to the text around them.
 */
export const keepSettingsBlock = (
  text: string,
  original: string | null | undefined
): string => {
  const block = settingsBlockOf(original);
  const body = text.trim();
  if (!block) return body;
  return body ? `${body}\n\n${block}` : block;
};

/** The solution-version fields an older solution's identity is saved to. */
export const legacyIdentityFields = (
  identity: AgentIdentity,
  current: IdentityFields | null | undefined
): IdentityFields => ({
  brandName: identity.brandName.trim(),
  supervisorName: identity.assistantName.trim(),
  otherInformation: keepSettingsBlock(
    identity.description,
    current?.otherInformation
  ),
  ...(identity.tone !== undefined ? { tone: identity.tone.trim() } : {}),
  ...(identity.instructions !== undefined
    ? {
        knowledgeAgentContext: keepSettingsBlock(
          identity.instructions,
          current?.knowledgeAgentContext
        ),
      }
    : {}),
});
